import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, UserPlus, Loader2, MailOpen } from "lucide-react";
import { useTeamManagement } from "@/hooks/use-team-management";
import { TeamMembersTable } from "./TeamMembersTable";
import { InviteUserDialog } from "./InviteUserDialog";
import { DeleteUserDialog } from "./DeleteUserDialog";
import { PendingInvitesTable } from "./PendingInvitesTable";

export function TeamManagement() {
  const {
    currentUser,
    teamMembers,
    pendingInvites,
    isLoading,
    hasCompany,
    inviteDialogOpen,
    deleteDialogOpen,
    userToDelete,
    isInviting,
    isDeleting,
    isUpdatingRole,
    isResending,
    isCancelling,
    isAdmin,
    canManageUser,
    handleInviteSubmit,
    handleDeleteUser,
    handleRoleChange,
    handleResendInvite,
    handleCancelInvite,
    openDeleteDialog,
    closeDeleteDialog,
    openInviteDialog,
    closeInviteDialog,
  } = useTeamManagement();

  if (!hasCompany) {
    return (
      <div className="max-w-7xl mx-auto">
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Company Found</h3>
            <p className="text-muted-foreground">
              You need to be associated with a company to manage team members.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between max-sm:flex-col max-sm:items-stretch max-sm:gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Team Members
              </CardTitle>
              <CardDescription>
                Manage your team members and their permissions
              </CardDescription>
            </div>
            {isAdmin && (
              <Button onClick={openInviteDialog} className="max-sm:w-full">
                <UserPlus className="h-4 w-4 mr-2" />
                Invite User
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {teamMembers.length > 0 ? (
            <TeamMembersTable
              members={teamMembers}
              currentUserId={currentUser?.id}
              isAdmin={isAdmin}
              canManageUser={canManageUser}
              onRoleChange={handleRoleChange}
              onDeleteUser={openDeleteDialog}
              isUpdating={isUpdatingRole}
              isDeleting={isDeleting}
            />
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No team members found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {isAdmin && pendingInvites.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MailOpen className="h-5 w-5" />
              Pending Invitations
              <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-blue-600 rounded-full">
                {pendingInvites.length}
              </span>
            </CardTitle>
            <CardDescription>
              Email invitations awaiting acceptance
            </CardDescription>
          </CardHeader>
          <CardContent>
            <PendingInvitesTable
              invites={pendingInvites}
              onCancelInvite={handleCancelInvite}
              isCancelling={isCancelling}
              onResendInvite={handleResendInvite}
              isResending={isResending}
            />
          </CardContent>
        </Card>
      )}

      <InviteUserDialog
        open={inviteDialogOpen}
        onOpenChange={closeInviteDialog}
        onSubmit={handleInviteSubmit}
        isInviting={isInviting}
      />

      <DeleteUserDialog
        open={deleteDialogOpen}
        onOpenChange={closeDeleteDialog}
        user={userToDelete}
        onConfirm={handleDeleteUser}
        isDeleting={isDeleting}
      />
    </div>
  );
}
